import type { Client } from '@openharness/client'
import type { FakeClient } from '@openharness/client/testing'
import type { McpServer } from '@openharness/protocol'
import { newMcpServerId } from '@openharness/protocol'

/**
 * The dev-only fake client.
 *
 * With `VITE_OPENHARNESS_FAKE=1`, `yarn dev` runs the whole UI against
 * `@openharness/client/testing`'s in-memory server instead of a real one: the same `Client`
 * interface, the same event order per turn, the same `after_seq` backlog — so the chat can be
 * developed, clicked through and QA'd before the server exists.
 *
 * Two things keep the fake out of a production build: `import.meta.env.DEV` is replaced by
 * `false`, which leaves the branch below statically dead so (and the dynamic import inside it)
 * is dropped, and the fake itself is a dynamic import, so it can only ever be a separate chunk.
 */
export function isFakeMode(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_OPENHARNESS_FAKE === '1'
}

/**
 * Which fake account to run: the seeded one, or a first run with nothing configured.
 *
 * `VITE_OPENHARNESS_FAKE_STATE=empty` is the state the first-run screen exists for (epic #201,
 * X5): an account with no provider key, so no catalog, no default and no chat that could run.
 * Without it fake mode is the seeded account (`seedFakeScenario`), which is the state to
 * *develop* in — the empty one is the state to look at.
 */
function fakeState(): 'seeded' | 'empty' {
  return import.meta.env.VITE_OPENHARNESS_FAKE_STATE === 'empty' ? 'empty' : 'seeded'
}

/**
 * Build the fake client for a dev run, or `null` when the app is talking to a real server.
 *
 * The condition is written out here rather than hidden in a helper: it has to be statically
 * false in a production build for the import below to be tree-shaken away.
 */
export async function createDevFakeClient(): Promise<Client | null> {
  if (!import.meta.env.DEV || import.meta.env.VITE_OPENHARNESS_FAKE !== '1') {
    return null
  }

  const { createFakeClient } = await import('@openharness/client/testing')

  if (fakeState() === 'empty') {
    const fresh = createFakeClient({
      // No credentials and no default: the first-run screen is decided by the credentials list
      // alone (#209), so this is the state it exists for.
      //
      // The catalog is seeded anyway, which a real server would not do (C5 lists models for
      // the providers the caller has a key for) — the fake's catalog is a fixed list that a
      // saved key cannot change, and without it the screen *after* the flow would have
      // nothing to name and nothing to run: `createFakeClient` picks the first catalog model
      // of the provider just saved, the way the server does (U4). Clicking the flow through
      // therefore ends where it would against a real server, which is the point of the state.
      models: [
        {
          id: 'anthropic/claude-sonnet-5',
          provider: 'anthropic',
          name: 'Claude Sonnet 5',
          context_window: 200_000,
          max_output_tokens: 64_000,
          cost: { input: 2, output: 10, cache_read: 0.2, cache_write: 2.5 },
          source: 'provider',
        },
        {
          id: 'openai/gpt-5.1-mini',
          provider: 'openai',
          name: 'GPT-5.1 mini',
          context_window: 400_000,
          max_output_tokens: 128_000,
          cost: { input: 0.4, output: 1.6, cache_read: 0.1, cache_write: null },
          source: 'provider',
        },
        {
          id: 'google/gemini-2.5-pro',
          provider: 'google',
          name: 'Gemini 2.5 Pro',
          context_window: 1_000_000,
          max_output_tokens: 65_536,
          cost: null,
          source: 'provider',
        },
      ],
      preferences: { default_model: null },
    })
    Object.assign(globalThis, { __openharnessFake: fresh })
    return fresh
  }

  // A small catalog across two providers — one of them fallen back to the registry — so the
  // New chat picker's grouping, context windows and fallback note are all visible in fake
  // mode (#91), not just a single row.
  const fake = createFakeClient({
    // New chat opens on the default (epic #116): the seeded catalog is three models, so
    // without one fake mode would start in the "pick a model" state (#146) instead of an
    // immediate chat — a state worth clicking through, but not the one to start on.
    preferences: { default_model: 'openai/gpt-5.1-mini' },
    models: [
      {
        id: 'anthropic/claude-sonnet-5',
        provider: 'anthropic',
        name: 'Claude Sonnet 5',
        context_window: 200_000,
        max_output_tokens: 64_000,
        cost: { input: 2, output: 10, cache_read: 0.2, cache_write: 2.5 },
        source: 'provider',
      },
      {
        id: 'openai/gpt-5.1',
        provider: 'openai',
        name: 'GPT-5.1',
        context_window: 400_000,
        max_output_tokens: 128_000,
        cost: { input: 2.5, output: 15, cache_read: 0.25, cache_write: null },
        source: 'registry',
      },
      {
        id: 'openai/gpt-5.1-mini',
        provider: 'openai',
        name: 'GPT-5.1 mini',
        context_window: 400_000,
        max_output_tokens: 128_000,
        cost: { input: 0.4, output: 1.6, cache_read: 0.1, cache_write: null },
        source: 'registry',
      },
      // A deliberately narrow one, so Settings → Context's warning is reachable in fake mode
      // (#282): chosen as a summary model it cannot fold the chat's broader budget within the
      // pass limit, and the card says so with the engine's own arithmetic.
      {
        id: 'openai/gpt-5.1-nano',
        provider: 'openai',
        name: 'GPT-5.1 nano',
        context_window: 8_000,
        max_output_tokens: 2_000,
        cost: { input: 0.1, output: 0.4, cache_read: null, cache_write: null },
        source: 'registry',
      },
    ],
    providers: [
      {
        provider: 'anthropic',
        status: 'ok',
        fetched_at: '2026-10-04T10:00:00.000Z',
        message: null,
      },
      {
        provider: 'openai',
        status: 'fallback',
        fetched_at: null,
        message: 'The provider timed out.',
      },
    ],
    // Three remote MCP servers (#303, X10; #313), one per auth type and one of them down, so
    // Settings → MCP servers shows a connected row with tools and their cost, a headers row with
    // the names it holds and never a value, and an error with its last reason. Their tools are
    // what the deployment offers too: the fake derives `GET /v1/me/tools`' remote half from the
    // servers it holds, so the Tools card's groups render from the same seed.
    mcpServers: DEV_MCP_SERVERS,
    mcpUnreachable: ['git'],
  })
  await seedFakeScenario(fake)

  // Handy while clicking through the UI: `__openharnessFake.history()` in the console.
  Object.assign(globalThis, { __openharnessFake: fake })

  return fake
}

/**
 * The remote MCP servers fake mode starts with (epic #303, X10; #313).
 *
 * Three, one per auth type, so the screen has something real to show in each state:
 *
 * - **notes** — an `oauth` server that is connected and lists two tools, which is where a
 *   permission is changed and a definition's cost is read;
 * - **wiki** — a `headers` server: a listing reports the header **names** and never a value;
 * - **git** — a `none` server that is down, so the status, the last error and a failing Test are
 *   all reachable (`mcpUnreachable` keeps it down however often it is tested).
 *
 * The ids come from the protocol's own generator, because the fake parses this seed with the
 * resource's schema and an `mcps_` id is a ULID — a hand-written one would have to be a real one
 * too, and a generator says where the shape comes from.
 */
const DEV_MCP_SERVERS: readonly McpServer[] = [
  {
    id: newMcpServerId(),
    type: 'mcp_server',
    owner_id: 'fake_user',
    name: 'notes',
    url: 'https://mcp.example.com/mcp',
    auth: 'oauth',
    enabled: true,
    status: 'connected',
    last_error: null,
    header_names: [],
    tools: [
      { name: 'search', description: 'Search your notes', definition_tokens: 41 },
      { name: 'read', description: 'Read one note', definition_tokens: 12 },
    ],
    definition_tokens: 53,
    last_tested_at: '2026-10-10T09:00:00.000Z',
    created_at: '2026-10-08T09:00:00.000Z',
    updated_at: '2026-10-10T09:00:00.000Z',
  },
  {
    id: newMcpServerId(),
    type: 'mcp_server',
    owner_id: 'fake_user',
    name: 'wiki',
    url: 'https://wiki.internal.example/mcp',
    auth: 'headers',
    enabled: true,
    status: 'connected',
    last_error: null,
    header_names: ['Authorization'],
    tools: [{ name: 'page', description: 'Fetch a wiki page', definition_tokens: 28 }],
    definition_tokens: 28,
    last_tested_at: '2026-10-10T09:05:00.000Z',
    created_at: '2026-10-08T09:10:00.000Z',
    updated_at: '2026-10-10T09:05:00.000Z',
  },
  {
    id: newMcpServerId(),
    type: 'mcp_server',
    owner_id: 'fake_user',
    name: 'git',
    url: 'https://git.internal.example/mcp',
    auth: 'none',
    enabled: false,
    status: 'error',
    last_error: 'The server could not be reached.',
    header_names: [],
    tools: [],
    definition_tokens: 0,
    last_tested_at: '2026-10-10T09:06:00.000Z',
    created_at: '2026-10-08T09:20:00.000Z',
    updated_at: '2026-10-10T09:06:00.000Z',
  },
]

/**
 * The markdown the seeded session's reply is made of.
 *
 * Everything the chat's renderer has to get right, in one message: a heading, a list, a table
 * that is wider than the bubble, inline code, and three fenced blocks with different languages.
 * Keeping it in the seeded reply means fake mode opens on a finished, fully rendered message
 * — which is also what the screenshots in the QA pass are taken of (#204, epic #201 X9).
 *
 * The Rust block is there because of #227: `rust` used to render plain, so it is the language
 * a reader who saw the old build will look for first, and the screenshot of "the chat now
 * highlights more" is a screenshot of it.
 */
const SEEDED_REPLY = [
  'I answer from the fake client: a scripted stream, no server involved.',
  '',
  '## What this reply shows',
  '',
  '- a heading, a list and some `inline code`',
  '- a table, which scrolls sideways rather than squashing the message',
  '- two code blocks, highlighted per theme, each with a **Copy** button',
  '',
  '| package | what it holds | where it is |',
  '| --- | --- | --- |',
  '| `@openharness/client` | the transcript reducer every frontend reads | `packages/client` |',
  '| `@openharness/protocol` | the event and session schemas | `packages/protocol` |',
  '| `@openharness/session` | the append-only event log | `packages/session` |',
  '',
  '```typescript',
  'export function greeting(name: string): string {',
  '  return `hello ${name}`',
  '}',
  '```',
  '',
  '```bash',
  'yarn install --immutable',
  'yarn turbo run build test --filter=@openharness/web...',
  '```',
  '',
  '```rust',
  'fn main() {',
  '    let greetings = vec!["hello", "hola"];',
  '    for greeting in &greetings {',
  '        println!("{greeting}");',
  '    }',
  '}',
  '```',
  '',
  '- press **Stop** while a reply is running',
  '- reload the page: the history is replayed from the log',
].join('\n')

/**
 * The scenario the fake starts with.
 *
 * The smallest one that exercises the UI: a second agent, a second session created from it —
 * an agent-created session still has to open and work (#91) — a turn already in its log so
 * the sidebar is not empty, and two scripted replies so the first messages stream instead of
 * arriving whole. When the scripts run out the fake answers `Fake reply: <your message>` on
 * its own.
 *
 * Exported so a test can seed one and read the scenario back.
 */
export async function seedFakeScenario(fake: FakeClient): Promise<void> {
  const assistant = await fake.agents.create({
    name: 'Assistant',
    description: 'A second agent, so the new-chat picker has something to pick.',
    // A catalog model, so the seeded session's label is a display name, not an id.
    model: { id: 'openai/gpt-5.1-mini' },
    system: 'You are a helpful assistant.',
  })

  const seeded = await fake.sessions.create({
    agent: assistant.id,
    title: 'What can you do?',
  })
  fake.respondWith(SEEDED_REPLY, { sessionId: seeded.id, chunks: 16, delayMs: 5 })
  await fake.sendMessage(seeded.id, 'What can you do?')
  await fake.waitForIdle(seeded.id)

  // Slow enough to watch arrive, and cut so that a fence is open part-way through: the
  // second message is what a screenshot of the mid-stream state is taken of.
  fake.respondWith(
    'Fake client again: still no server, still streaming.\n\n' +
      '```bash\nkubectl apply -f deploy.yaml\nkubectl rollout status deploy/web\n```\n\n' +
      'That is the whole of it — nothing here reached a server.',
    { chunks: 18, delayMs: 120 },
  )
}
